import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useUserSettings } from '../contexts/UserSettingsContext';
import { normalizeUserSettings, type UserSettings } from '../types/userSettings';
import { DEFAULT_USER_SETTINGS } from '../types/userSettings';

type SettingsDialogProps = {
  open: boolean;
  onClose: () => void;
};

function parseMaxPerRowValue(value: string): number | undefined {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) {
    return undefined;
  }

  return Math.max(1, Math.min(10, Math.round(parsedValue)));
}

export default function SettingsDialog({ open, onClose }: SettingsDialogProps) {
  const { settings, saveSettings, isLoading } = useUserSettings();
  const [draft, setDraft] = useState<UserSettings>(settings);
  const [isSaving, setIsSaving] = useState(false);
  const [uiMaxPerRowInput, setUiMaxPerRowInput] = useState(String(settings.ui.maxTAccountsPerRow));
  const [exportMaxPerRowInput, setExportMaxPerRowInput] = useState(String(settings.export.maxTAccountsPerRow));
  const checkboxRowSx = {
    display: 'flex',
    alignItems: 'center',
    '& .MuiCheckbox-root': {
      py: 0.5,
      px: 0.5,
      borderRadius: 1,
      '&:hover': {
        borderRadius: 1,
      },
    },
    '& .MuiFormControlLabel-label': {
      display: 'inline-flex',
      alignItems: 'center',
      lineHeight: 1.2,
      transform: 'translateY(1px)',
    },
  };

  useEffect(() => {
    if (open) {
      setDraft(settings);
      setUiMaxPerRowInput(String(settings.ui.maxTAccountsPerRow));
      setExportMaxPerRowInput(String(settings.export.maxTAccountsPerRow));
    }
  }, [open, settings]);

  const commitUiMaxPerRow = (rawValue: string) => {
    const nextValue = parseMaxPerRowValue(rawValue) ?? DEFAULT_USER_SETTINGS.ui.maxTAccountsPerRow;
    setUiMaxPerRowInput(String(nextValue));
    setDraft((previousDraft) => ({
      ...previousDraft,
      ui: {
        ...previousDraft.ui,
        maxTAccountsPerRow: nextValue,
      },
    }));
  };

  const commitExportMaxPerRow = (rawValue: string) => {
    const nextValue = parseMaxPerRowValue(rawValue) ?? DEFAULT_USER_SETTINGS.export.maxTAccountsPerRow;
    setExportMaxPerRowInput(String(nextValue));
    setDraft((previousDraft) => ({
      ...previousDraft,
      export: {
        ...previousDraft.export,
        maxTAccountsPerRow: nextValue,
      },
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    const normalizedDraft = normalizeUserSettings({
      ui: {
        maxTAccountsPerRow: parseMaxPerRowValue(uiMaxPerRowInput) ?? DEFAULT_USER_SETTINGS.ui.maxTAccountsPerRow,
        showDecimals: draft.ui.showDecimals,
        formatNumbersWithCommas: draft.ui.formatNumbersWithCommas,
        themeMode: draft.ui.themeMode,
      },
      export: {
        entriesSeparateTab: draft.export.entriesSeparateTab,
        formatNumbersWithCommas: draft.export.formatNumbersWithCommas,
        maxTAccountsPerRow: parseMaxPerRowValue(exportMaxPerRowInput) ?? DEFAULT_USER_SETTINGS.export.maxTAccountsPerRow,
      },
    });

    const wasSaved = await saveSettings(normalizedDraft);
    setIsSaving(false);

    if (wasSaved) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Settings</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" sx={{ mt: 0.25, mb: 0.5, fontWeight: 700 }}>
          UI
        </Typography>

        <Typography variant="body2" sx={{ mb: 0.25 }}>
          Maximum T-Accounts per row (1-10, default 4)
        </Typography>
        <TextField
          size="small"
          margin="dense"
          type="number"
          value={uiMaxPerRowInput}
          inputProps={{ min: 1, max: 10, step: 1, 'aria-label': 'Maximum T-Accounts per row (1-10, default 4)' }}
          onFocus={(event) => {
            event.currentTarget.select();
          }}
          onChange={(event) => {
            const rawValue = event.target.value;
            if (!/^\d{0,2}$/u.test(rawValue)) {
              return;
            }

            setUiMaxPerRowInput(rawValue);
          }}
          onBlur={() => commitUiMaxPerRow(uiMaxPerRowInput)}
          sx={{
            display: 'block',
            mt: 0,
            width: 160,
            maxWidth: '100%',
            '& .MuiInputBase-input': {
              py: 0.75,
            },
          }}
        />

        <FormControlLabel
          control={(
            <Checkbox
              checked={draft.ui.showDecimals}
              onChange={(event) => {
                const nextChecked = event.target.checked;
                setDraft((previousDraft) => ({
                  ...previousDraft,
                  ui: {
                    ...previousDraft.ui,
                    showDecimals: nextChecked,
                  },
                }));
              }}
            />
          )}
          label="Show decimals"
          sx={{ ...checkboxRowSx, mt: 0.25 }}
        />

        <FormControlLabel
          control={(
            <Checkbox
              checked={draft.ui.formatNumbersWithCommas}
              onChange={(event) => {
                const nextChecked = event.target.checked;
                setDraft((previousDraft) => ({
                  ...previousDraft,
                  ui: {
                    ...previousDraft.ui,
                    formatNumbersWithCommas: nextChecked,
                  },
                }));
              }}
            />
          )}
          label="Format numbers with commas"
          sx={{ ...checkboxRowSx, mt: 0 }}
        />

        <Box sx={{ mt: 0.75 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Theme
          </Typography>
          <ToggleButtonGroup
            exclusive
            value={draft.ui.themeMode}
            onChange={(_event, value: 'light' | 'dark' | null) => {
              if (!value) {
                return;
              }

              setDraft((previousDraft) => ({
                ...previousDraft,
                ui: {
                  ...previousDraft.ui,
                  themeMode: value,
                },
              }));
            }}
            size="small"
          >
            <ToggleButton value="light">Light</ToggleButton>
            <ToggleButton value="dark">Dark</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Typography variant="subtitle1" sx={{ mt: 2, mb: 0.5, fontWeight: 700 }}>
          Export
        </Typography>

        <FormControlLabel
          control={(
            <Checkbox
              checked={draft.export.entriesSeparateTab}
              onChange={(event) => {
                const nextChecked = event.target.checked;
                setDraft((previousDraft) => ({
                  ...previousDraft,
                  export: {
                    ...previousDraft.export,
                    entriesSeparateTab: nextChecked,
                  },
                }));
              }}
            />
          )}
          label="Entries listing on separate tab for XLSX"
          sx={checkboxRowSx}
        />

        <FormControlLabel
          control={(
            <Checkbox
              checked={draft.export.formatNumbersWithCommas}
              onChange={(event) => {
                const nextChecked = event.target.checked;
                setDraft((previousDraft) => ({
                  ...previousDraft,
                  export: {
                    ...previousDraft.export,
                    formatNumbersWithCommas: nextChecked,
                  },
                }));
              }}
            />
          )}
          label="Format numbers with commas in exports (PNG, PDF, XLSX)"
          sx={{ ...checkboxRowSx, mt: 0.25 }}
        />

        <Typography variant="body2" sx={{ mt: 1, mb: 0.25 }}>
          Maximum T-Accounts per row (1-10, default 4)
        </Typography>
        <TextField
          size="small"
          margin="dense"
          type="number"
          value={exportMaxPerRowInput}
          inputProps={{ min: 1, max: 10, step: 1, 'aria-label': 'Maximum T-Accounts per row (1-10, default 4)' }}
          onFocus={(event) => {
            event.currentTarget.select();
          }}
          onChange={(event) => {
            const rawValue = event.target.value;
            if (!/^\d{0,2}$/u.test(rawValue)) {
              return;
            }

            setExportMaxPerRowInput(rawValue);
          }}
          onBlur={() => commitExportMaxPerRow(exportMaxPerRowInput)}
          sx={{
            display: 'block',
            mt: 0,
            width: 160,
            maxWidth: '100%',
            '& .MuiInputBase-input': {
              py: 0.75,
            },
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => {
            void handleSave();
          }}
          disabled={isSaving || isLoading}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
